// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/guestClaimState.ts
// 🔧 PURPOSE: La vista ospite deve sapere se da questo telefono è partito un
//             check-in online che aspetta lo smistamento: allora la card
//             "Hai già prenotato?" diventa "Check-in inviato · In attesa di
//             conferma" (mockup Attesa).
//
//             Legge le voci di lib/checkInClaimStorage e conta come "in
//             attesa" SOLO quelle a cui il server ha già risposto `pending`
//             (`status: 'pending'`, lo scrive il watcher). Una voce creata
//             aprendo il check-in e mai inviata non basta: la Home
//             prometterebbe un accesso che non arriverà (critica 14).
//             La lettura qui è pura (nessuna scrittura durante il render):
//             le voci vecchie le toglie lo storage quando le usa.
// ============================================

import { useSyncExternalStore } from 'react';
import { GUEST_SESSION_CHANGED_EVENT } from '../../hooks/useGuestSession';
import { CHECKIN_CLAIMS_EVENT, CHECKIN_CLAIMS_KEY, CLAIM_MAX_AGE_MS } from '../../lib/checkInClaimStorage';

export { CHECKIN_CLAIMS_KEY, CHECKIN_CLAIMS_EVENT };

interface StoredClaim {
  createdAt?: number | string;
  status?: string;
}

// Lettura ricordata sul testo salvato: React chiede lo stato a ogni render.
let lastRaw: string | null | undefined;
let lastPending = false;

const readRaw = (): string | null => {
  try {
    return localStorage.getItem(CHECKIN_CLAIMS_KEY);
  } catch {
    return null;
  }
};

const isPending = (entry: unknown, now: number): boolean => {
  if (!entry || typeof entry !== 'object') return false;
  const claim = entry as StoredClaim;
  if (claim.status !== 'pending') return false;
  const created = typeof claim.createdAt === 'string' ? Date.parse(claim.createdAt) : Number(claim.createdAt);
  // Senza data si tiene per buona: la toglie il watcher (404) o l'accesso.
  return !isFinite(created) || now - created < CLAIM_MAX_AGE_MS;
};

/** Vero se almeno un check-in inviato da qui aspetta lo smistamento. */
export function readClaimPending(): boolean {
  const raw = readRaw();
  if (raw === lastRaw) return lastPending;
  lastRaw = raw;
  lastPending = false;
  if (!raw) return false;
  try {
    const list = JSON.parse(raw);
    const now = Date.now();
    lastPending = Array.isArray(list) && list.some((entry) => isPending(entry, now));
  } catch {
    lastPending = false;
  }
  return lastPending;
}

const subscribe = (onChange: () => void): (() => void) => {
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key === CHECKIN_CLAIMS_KEY) onChange();
  };
  window.addEventListener(CHECKIN_CLAIMS_EVENT, onChange);
  // Un accesso o un'uscita svuotano le richieste (useGuestSession).
  window.addEventListener(GUEST_SESSION_CHANGED_EVENT, onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(CHECKIN_CLAIMS_EVENT, onChange);
    window.removeEventListener(GUEST_SESSION_CHANGED_EVENT, onChange);
    window.removeEventListener('storage', onStorage);
  };
};

/** Lo stato "check-in inviato, in attesa di conferma", aggiornato dagli eventi. */
export function useGuestClaimPending(): boolean {
  return useSyncExternalStore(subscribe, readClaimPending, () => false);
}
